import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import isLifetimeSubscription from '../helpers/isLifetimeSubscription'
import localizeDate from '../helpers/localizeDate'
import { SubscriptionInfo } from '../helpers/revenueCat'
import Dialog from './Dialog'

interface Props {
  open: boolean
  onClose: () => void
  subscriptions: SubscriptionInfo[]
}

export default function SubscriptionDialog({
  open,
  onClose,
  subscriptions,
}: Props) {
  const { t } = useTranslation()

  const expiresDate = (subscription: SubscriptionInfo) =>
    subscription.expiresDate || new Date() // RevenueCat uses Date objects directly
  const isActive = (subscription: SubscriptionInfo) => subscription.isActive
  const isRenewing = (subscription: SubscriptionInfo) => subscription.willRenew
  const purchaseDate = (subscription: SubscriptionInfo) =>
    subscription.purchaseDate // RevenueCat uses Date objects directly

  const store = (subscription: SubscriptionInfo) => {
    switch (subscription.store) {
      case null:
      case undefined:
      case 'unknown':
        return t('pages.account.subscription.dialog.unknown').toUpperCase()
      case 'promotional':
      case 'stripe':
      case 'rc_billing':
        return 'WEB'
      case 'app_store':
      case 'mac_app_store':
        return 'App Store'
      case 'play_store':
        return 'Play Store'
      default:
        return ''
    }
  }

  const activeSubscriptions = subscriptions.filter(isActive)
  const showMultipleSubscriptionsWarning = activeSubscriptions.length > 1
  const subscriptionsNewestFirst = [...subscriptions].sort(
    (leftSubscription, rightSubscription) =>
      rightSubscription.purchaseDate.getTime() -
      leftSubscription.purchaseDate.getTime(),
  )

  return (
    <Dialog onClose={onClose} open={open}>
      <>
        <h1 className="text-center text-[25px] font-bold">
          {t('pages.account.subscription.dialog.heading')}
        </h1>
        {showMultipleSubscriptionsWarning && (
          <p className="-mt-4 text-center font-bold text-red-600">
            {t('pages.account.subscription.dialog.multipleSubscriptions', {
              count: activeSubscriptions.length,
            })}
          </p>
        )}

        {subscriptionsNewestFirst.map((subscription) => (
          <div key={subscription.storeTransactionId} className="mt-7 mb-9">
            <table className="w-full text-left">
              <tbody>
                <tr>
                  <th className="w-px pr-6 font-bold whitespace-nowrap">
                    {t('pages.account.subscription.dialog.type')}
                  </th>

                  <td className="py-3">{store(subscription)}</td>
                </tr>
                <tr>
                  <th className="w-px pr-6 font-bold whitespace-nowrap">
                    {t('pages.account.subscription.dialog.status')}
                  </th>

                  <td className="py-3">
                    {t(
                      `pages.account.subscription.dialog.${isActive(subscription) ? 'statusActive' : 'statusCanceled'}`,
                    )}
                  </td>
                </tr>

                <tr>
                  <th className="w-px pr-6 font-bold whitespace-nowrap">
                    {t('pages.account.subscription.dialog.begins')}
                  </th>
                  <td className="py-3">
                    {localizeDate(purchaseDate(subscription))}
                  </td>
                </tr>

                {!isLifetimeSubscription(subscription) && (
                  <tr>
                    <th className="w-px pr-6 font-bold whitespace-nowrap">
                      {t(
                        `pages.account.subscription.dialog.${isRenewing(subscription) ? 'renews' : 'ends'}`,
                      )}
                    </th>

                    <td className="py-3">
                      {localizeDate(expiresDate(subscription))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
        <div className="text-center">
          <Link
            className="font-bold underline"
            to="/help-center"
            data-testid="button-manage"
          >
            {t('pages.account.subscription.dialog.needHelpContactUs')}
          </Link>
        </div>
      </>
    </Dialog>
  )
}
